<template>
  <div ref="container" class="overflow-y-scroll relative">
    <div class="mt-24" />

    <div class="text-sm text-gray-500 px-4 mx-auto" :class="loading ? '' : 'opacity-0'">
      <svg aria-hidden="true" class="mx-auto h-5 w-5 animate-spin fill-primary-600 text-gray-200 dark:text-gray-600 mb-6" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
      </svg>
    </div>

    <div v-if="!morePages" class="text-sm text-gray-500 p-4 mx-auto">
      <p class="text-center">
        <slot name="no-more">
          {{ $t('components.message.elements.infiniteScroll.noMoreData') }}
        </slot>
      </p>
    </div>

    <slot />
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

interface Props {
  fetch: (...args: any[]) => any
  pageCount?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  pageCount: null,
})

const emit = defineEmits<{
  (e: 'scroll-bottom'): void
}>()

const container = ref<HTMLElement>()
const page = ref(1)
const loading = ref(false)

const morePages = computed(() => {
  return props.pageCount ? page.value <= props.pageCount : true
})

const load = debounce(async (p: number) => {
  if (!morePages.value || loading.value) { return }

  const oldScrollHeight = container.value?.scrollHeight || 0

  loading.value = true

  await props.fetch(p)

  nextTick(() => {
    if (container.value) {
      if (p === 1) {
        container.value.scrollTop = container.value.scrollHeight || 0
      } else {
        container.value.scrollTop = container.value.scrollHeight - oldScrollHeight
      }
    }
  })

  loading.value = false

  page.value++
}, 50)

const scroll = () => {
  const el = container.value
  if (!el) return

  const scrollHeight = el.scrollHeight
  const scrollTop = el.scrollTop

  const scrollDistanceTotal = el.scrollHeight - el.clientHeight
  const scrollDistanceFromBottom = scrollDistanceTotal - el.scrollTop

  if (scrollDistanceFromBottom < 50) {
    emit('scroll-bottom')
  }

  if (scrollHeight > 20 && scrollTop == 0 && !loading.value) {
    load(page.value)
  }
}

const reload = () => {
  page.value = 1
  load(page.value)
}

onMounted(() => {
  load(page.value)
  container.value?.addEventListener('scroll', scroll)
})

onBeforeUnmount(() => {
  container.value?.removeEventListener?.('scroll', scroll)
})

defineExpose({ reload, $el: container })
</script>
